import React from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames/bind'
import { Add as AddIcon, Remove as RemoveIcon } from 'assets/images'
import Input from 'components/Input/Input'
import LanguageSelect from 'components/LanguageSelect/LanguageSelect'
import { useForm } from 'contexts'
import { trackGoal } from 'utils/fathom'
import styles from './Sidebar.module.scss'

const cx = classNames.bind(styles)

const Sidebar = ({ onDownload }) => {
  const { t, i18n } = useTranslation()

  const { formData, setField } = useForm()
  const { author, title, subtitle, details = [], image = {} } = formData

  const decreaseDetails = () => {
    if (!details.length) return

    setField(details.slice(0, details.length - 1), 'details')
  }

  const increaseDetails = () => {
    if (details.length > 1) return

    setField([...details, ''], 'details')
  }

  const renderDetails = () => (
    <div className={styles.details}>
      {details.map((detail, index) => (
        <Input
          key={index}
          id={`detail-${index}`}
          label={t('sidebar.form.detail', { number: index + 1 })}
          value={detail}
          onChange={val => {
            setField(
              Object.assign([...details], {
                [index]: val,
              }),
              'details',
            )
          }}
        />
      ))}
      <div className={styles.controls}>
        <button
          type="button"
          className={cx(['control', 'minus', { disabled: details.length < 1 }])}
          disabled={details.length < 1}
          onClick={decreaseDetails}
        >
          <RemoveIcon />
        </button>
        <button
          type="button"
          className={cx(['control', 'add', { disabled: details.length > 1 }])}
          disabled={details.length > 1}
          onClick={increaseDetails}
        >
          <AddIcon />
        </button>
      </div>
    </div>
  )

  const imageUpload = event => {
    trackGoal('JLTGGUDR')
    setField(event.target.files[0], 'image')
  }

  const uploadButton = () => (
    <div className={styles.upload}>
      {image.name && <span className={styles.imageName}>{image.name}</span>}
      <label htmlFor="file">
        {image.name
          ? t('sidebar.form.image.change')
          : t('sidebar.form.image.select')}
      </label>
      <input type="file" id="file" onChange={imageUpload} />
    </div>
  )

  return (
    <div className={styles.sidebar}>
      <div className={styles.upper}>
        <div className={styles.header}>
          <div className={styles.logo} />
          <h1 className={styles.title}>{t('sidebar.title')}</h1>
        </div>
        <Input
          id="author"
          label={t('sidebar.form.author')}
          value={author}
          onChange={val => setField(val, 'author')}
        />
        <Input
          id="title"
          label={t('sidebar.form.title')}
          value={title}
          onChange={val => setField(val, 'title')}
        />
        <Input
          id="subtitle"
          label={t('sidebar.form.subtitle')}
          value={subtitle}
          onChange={val => setField(val, 'subtitle')}
        />
        {renderDetails()}
        {uploadButton()}
      </div>
      <button className={styles.button} type="button" onClick={onDownload}>
        {t('sidebar.form.export')}
      </button>
      <p className={styles.legal}>{t('sidebar.footer.legal')}</p>
      <div className={styles.footer}>
        <div className={styles.credit}>
          <span>
            {`${t('sidebar.footer.created')} `}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://twitter.com/joshuat"
            >
              Joshua Turner
            </a>{' '}
            ↗
          </span>
          {i18n.language === 'es' && (
            <span>
              {`${t('sidebar.footer.translated')} `}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="http://saez.cat/"
              >
                Sebastián Sáez M.
              </a>{' '}
              ↗
            </span>
          )}
        </div>
        <LanguageSelect />
      </div>
      <span className={styles.plea}>{t('sidebar.footer.plea')}</span>
    </div>
  )
}

export default Sidebar
